import React, { ChangeEvent, FunctionComponent, useContext, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Box } from "@mui/material";
import { PictureAsPdf, GridOn, ErrorOutline } from "@mui/icons-material";

import { createDocuments, documentUploaded, uploadDocumentToS3, uploadSites } from "api";
import { EsgArrowDownWhite, EsgFileUpload } from "assets";
import { useAmplitude, useNotify, useSites } from "hooks";
import { useUserRole } from "recoils";
import { QUERY_KEYS } from "consts";
import { updateFileCountContext } from "providers/uploadFileCount-provider";
import { Body2Regular, useDialogControls, SelectionDialog } from "components";
import { EXCEL_FILE_TYPE } from "./const";

export const EsgDocumentsUpload: FunctionComponent<{
	isLoading?: boolean;
	onUploadCompleted: () => void;
}> = ({ isLoading, onUploadCompleted }) => {
	const notify = useNotify();
	const userRole = useUserRole();
	const queryClient = useQueryClient();
	const sitesDialogControls = useDialogControls();
	const { mutate: create } = useMutation(createDocuments);
	const { mutateAsync: uploadToS3 } = useMutation(uploadDocumentToS3);
	const { mutateAsync: uploaded } = useMutation(documentUploaded);
	const { data: sites } = useSites();
	const { mutateAsync: upload, isLoading: isLoadingUploadSites } = useMutation(uploadSites);
	const { trackEvent } = useAmplitude();

	const approvalBoxRef = useRef<HTMLDivElement | null>(null);

	const [selectedSite, setSelectedSite] = useState<null | number>(null);
	const [uploadDisable, setUploadDisable] = useState<boolean>(false);
	const [showBox, setShowBox] = useState<boolean>(false);
	const [openFirstDialog, setOpenFirstDialog] = useState<boolean>(false);
	const [openSecondDialog, setOpenSecondDialog] = useState<boolean>(false);
	const [overrideFlag, setOverrideFlag] = useState<boolean>(false);

	const handlClick = () => {
		setShowBox(!showBox);
	};

	const handleClickOutside = (event: MouseEvent) => {
		if (approvalBoxRef.current && !approvalBoxRef.current.contains(event.target as Node)) {
			setShowBox(false);
		}
	};

	const { updateUploadedCount, updateTotalCount } = useContext(updateFileCountContext);

	const fileInputRef = useRef<HTMLInputElement | null>(null);

	let isOneSite = false;

	if (sites !== undefined) {
		isOneSite = sites?.length > 1;
	}

	const onSiteSelect = (id: number) => {
		sitesDialogControls.close();
		setSelectedSite(id);
		fileInputRef?.current?.click();
	};

	const onUploadButtonClick = () => {
		setSelectedSite(0);
		fileInputRef?.current?.click();
	};

	// PDF Upload Flow
	const onChange = (e: ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(e.target.files || []);
		if (files.length === 0) {
			return;
		};

		trackEvent("Uploaded Document", "Documents - Upload", {
			uploadType: "PDF"
		});

		updateTotalCount(files.length);
		setUploadDisable(true);
		setShowBox(false);
		create(
			{ fileNames: files.map((file) => file.name), siteId: selectedSite ?? 1 },
			{
				onSuccess: (documents: Array<{ s3uploadUrl: string; document: { uuid: string; originalFileName: string } }>) => {
					const uploadPromises = documents.map(async (document: any) => {
						const matchingFile = files.find((file) => file.name === document.document.originalFileName);
						if (!matchingFile) {
							try {
								await uploaded({ documentUuid: document.document.uuid, hasError: "true", errorMessage: "Source file not found for document" });
								return false;
							} catch {
								return false;
							}
						}
						try {
							await uploadToS3({ url: document.s3uploadUrl, file: matchingFile });
							const res = await uploaded({ documentUuid: document.document.uuid, hasError: "false", errorMessage: "" });
							if (res.data[0].status === "PROCESSING") {
								// Track when AI processing begins
								trackEvent("AI Processing Started", "Documents - Upload");
								updateUploadedCount((prev: number) => prev + 1);
								return true;
							}
							return false;
						} catch (err) {
							try {
								await uploaded({ documentUuid: document.document.uuid, hasError: "true", errorMessage: (err as Error).message });
								return false;
							} catch {
								return false;
							}
						}
					});
					Promise.all(uploadPromises).then((results) => {
						const successCount = results.filter(Boolean).length;
						const errorCount = files.length - successCount;

						if (successCount > 0) {
							onUploadCompleted();
							if (selectedSite) {
								queryClient.invalidateQueries([QUERY_KEYS.UTILITY_BILLS]);
							}
						}
						if (errorCount === 0) {
							notify.success("Successfully uploaded all files.");
							setTimeout(() => { updateUploadedCount(0); updateTotalCount(0); }, 3000);
							setUploadDisable(false);
						} else {
							notify.error(`There was an error uploading ${errorCount} out of ${files.length} files. Please check the Jobs page for more details.`);
						}
						e.target.value = "";
					});
				},
				onError() {
					notify.error(`There was an error uploading ${files.length} out of ${files.length} files. Please check the Jobs page for more details.`);
					e.target.value = "";
				},
			},
		);
		setUploadDisable(false);
	};

	const extraReqParams = [["configuration", "false"]]

	// Excel Upload Flow
	const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
		const { files } = e.target;
		const formData = new FormData();

		if (files) {
			trackEvent("Uploaded Document", "Documents - Upload", {
				uploadType: "Excel File"
			});
			formData.append("file", files[0], files[0].name);
			formData.append("overrideFlag", overrideFlag as any);
			if (extraReqParams) {
				extraReqParams.forEach(([key, value]) => {
					formData.append(key, value);
				});
			};
			setUploadDisable(true);
			setShowBox(false);
			upload({
				file: formData,
				overrideFlag
			}, {
				onSuccess: () => {
					notify.success('Upload in Progress: Check the Jobs page for the status.');
					queryClient.invalidateQueries([QUERY_KEYS.DOCUMENTS]);
				},
				onError: (error) => {
					notify.error(`Failed to upload file: ${files[0].name}! (${(error as AxiosError)?.response?.data || "Internal Server Error"})`);
				},
				onSettled() {
					e.target.value = "";
				},
			});
		}
	};

	const handleFirstDialogClose = (choice: "Yes" | "Not Sure" | "No") => {
		setOpenFirstDialog(false);
		if (choice === "Yes") {
			setOpenSecondDialog(true);
			setOverrideFlag(true);
		} else {
			document.getElementById("file-input")?.click();
			setOverrideFlag(false);
		}
	};

	const handleSecondDialogClose = (choice: "Yes" | "No") => {
		setOpenSecondDialog(false);
		setOverrideFlag(false);
		document.getElementById("file-input")?.click();
		if (choice === "Yes") {
			setOverrideFlag(true);
		} else {
			setOverrideFlag(false);
		}
	};

	useEffect(() => {
		if (uploadDisable) {
			setUploadDisable(false)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading, uploadDisable])

	useEffect(() => {
		if (showBox) {
			document.addEventListener("mousedown", handleClickOutside);
		} else {
			document.removeEventListener("mousedown", handleClickOutside);
		}
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [showBox]);

	return (
		<>
			<Box
				ref={approvalBoxRef}
				data-testid="upload-box"
				sx={{ display: "flex", flexDirection: "column", gap: 1, width: "200px", position: "relative" }}>
				<Button
					sx={{
						borderRadius: 2,
						color: "white",
						width: "200px",
						height: "36px",
						p: 0

					}}
					variant="contained"
					data-testid="upload-data-button"
					startIcon={<EsgFileUpload />}
					endIcon={<Box
						sx={{
							ml: 2,
							mt: showBox ? "0" : "2px",
							transform: showBox ? "rotate(180deg)" : "rotate(0deg)",
							transition: "transform 0.3s ease"
						}}>
						<EsgArrowDownWhite />
					</Box>}
					onClick={handlClick}>
					<Body2Regular>Upload Data</Body2Regular>
				</Button>

				{showBox && (
					<Box
						sx={{
							width: "200px",
							position: "absolute",
							top: "100%",
							left: 0,
							mt: 1,
							p: 0.5,
							border: "1px solid #C1C1C1",
							borderRadius: "8px",
							backgroundColor: "#FBFBFB",
							zIndex: 10,
							boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
						}}
					>
						<>
							<input
								ref={fileInputRef}
								data-testid="upload-pdf-input"
								hidden
								accept="application/pdf"
								multiple
								type="file"
								onChange={onChange}
							/>
							<Button
								variant="contained"
								startIcon={<PictureAsPdf />}
								title="Upload PDF"
								data-testid="upload-pdf-button"
								fullWidth
								onClick={onUploadButtonClick}
								disabled={!sites?.length || userRole.isAuditor || uploadDisable}
							>
								Upload PDF
							</Button>
						</>
						<Button
							sx={{ mt: "4px" }}
							variant="contained"
							component="label"
							fullWidth
							startIcon={<GridOn />}
							data-testid="upload-excel-button"
							disabled={isLoadingUploadSites || userRole.isAuditor || uploadDisable}
							onClick={() => setOpenFirstDialog(true)}
						>
							Upload Excel Data
						</Button>
					</Box>
				)}
			</Box>

			{isOneSite && (
				<SelectionDialog
					options={(sites || []).map(site => ({ id: site.id, name: site.name || '' }))}
					isDialogOpen={sitesDialogControls.isOpen}
					onDialogClose={sitesDialogControls.close}
					onSingleSelect={onSiteSelect}
				/>
			)}

			<input id="file-input" data-testid="file-input" hidden accept={`${EXCEL_FILE_TYPE}, application/vnd.ms-excel`} type="file" onChange={onFileChange} />

			{/* First Dialog */}
			<Dialog
				data-testid="first-dialog"
				open={openFirstDialog}
				onClose={() => handleFirstDialogClose("No")}
				PaperProps={{
					sx: {
						width: 384,
						bgcolor: "#FFFFFF",
					},
				}}
			>
				<DialogContent sx={{ py: '2px', pl: 3 }}>
					<Box sx={{ display: "flex", alignItems: "center" }}>
						<ErrorOutline sx={{ color: "#C55050" }} />
						<DialogTitle sx={{ px: '10px', fontSize: '16px', fontWeight: 600, fontFamily: 'Inter', m: 0 }}>
							Data Points Confirmed?
						</DialogTitle>
					</Box>
					<DialogContentText sx={{ fontSize: '14px', fontWeight: 400, fontFamily: 'Inter', opacity: 0.65 }}>
						Are you uploading confirmed data points (e.g., historically verified data, data from metering systems)?
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						sx={{ color: "#6B7280", fontSize: '14px', fontWeight: 600 }}
						onClick={() => handleFirstDialogClose("Not Sure")}
						variant="text"
						data-testid="first-dialog-not-sure"
					>
						Not Sure
					</Button>
					<Button
						sx={{ color: "#6B7280", fontSize: '14px', fontWeight: 600 }}
						onClick={() => handleFirstDialogClose("No")}
						variant="text"
						data-testid="first-dialog-no"
					>
						No
					</Button>
					<Button
						sx={{
							borderRadius: "8px",
							background: "#C55050",
							color: "white",
							fontSize: '14px',
							fontWeight: 600,
							"&:hover": { background: "#C55050" },
						}}
						onClick={() => handleFirstDialogClose("Yes")}
						variant="contained"
						data-testid="first-dialog-yes"
					>
						Yes
					</Button>
				</DialogActions>
			</Dialog>


			{/* Second Dialog */}
			<Dialog
				data-testid="second-dialog"
				open={openSecondDialog} onClose={() => handleSecondDialogClose("No")}
				PaperProps={{
					sx: {
						width: 384,
						bgcolor: "#FFFFFF",
					},
				}}
			>
				<DialogContent sx={{ py: '2px', pl: 3 }}>
					<Box sx={{ display: "flex", alignItems: "center" }}>
						<ErrorOutline sx={{ color: "#C55050" }} />
						<DialogTitle sx={{ px: '10px', fontSize: '16px', fontWeight: 600, fontFamily: 'Inter', m: 0 }}>
							Override All Flags?
						</DialogTitle>
					</Box>
					<DialogContentText sx={{ fontSize: '14px', fontWeight: 400, fontFamily: 'Inter', opacity: 0.65 }}>
						Do you want to override all anomaly flags? (e.g. Duplicate detection, YoY/MoM threshold detection, etc.)
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						sx={{ color: "#6B7280", fontSize: '14px', fontWeight: 600 }}
						onClick={() => handleSecondDialogClose("No")}
						variant="text"
						data-testid="second-dialog-no"
					>
						No
					</Button>
					<Button
						sx={{
							borderRadius: "8px",
							background: "#C55050",
							color: "white",
							fontSize: '14px',
							fontWeight: 600,
							"&:hover": { background: "#C55050" },
						}}
						onClick={() => handleSecondDialogClose("Yes")}
						variant="contained"
						data-testid="second-dialog-yes"
					>
						Yes
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
